import { useEffect, useMemo, useState, useTransition } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import BrandMark from '../components/BrandMark.jsx';
import { api } from '../lib/api.js';
import { formatMatterMoney } from '../lib/matterCurrency.js';
import { demandChoiceFields, getLocalSettings } from '../lib/settingsStore.js';

const QBO_SESSION_KEY = 'demandjet.qbo.session';

function formatCurrency(value) {
  return formatMatterMoney(value, 'USD');
}

function readSession() {
  try {
    const raw = localStorage.getItem(QBO_SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (session.expiresAt && session.expiresAt < Date.now()) {
      localStorage.removeItem(QBO_SESSION_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

function writeSession(session) {
  localStorage.setItem(QBO_SESSION_KEY, JSON.stringify(session));
}

function clearSession() {
  localStorage.removeItem(QBO_SESSION_KEY);
}

function decodeSessionParam(value) {
  const padded = String(value)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const json = decodeURIComponent(
    Array.prototype.map
      .call(atob(padded), (c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  );
  return JSON.parse(json);
}

function demoInvoices() {
  return [
    {
      id: '9002',
      docNumber: '5610',
      externalInvoiceId: 'demo-realm:9002',
      debtorName: 'Summit Freight Co.',
      debtorState: 'TX',
      amountOwed: 9750,
      dueDate: '2026-07-12',
      balance: 9750,
      demandSent: false,
    },
    {
      id: '9003',
      docNumber: '5688',
      externalInvoiceId: 'demo-realm:9003',
      debtorName: 'Lakeside Outfitters',
      debtorState: 'NY',
      amountOwed: 4200,
      dueDate: '2026-07-18',
      balance: 4200,
      demandSent: false,
    },
    {
      id: '9001',
      docNumber: '5542',
      externalInvoiceId: 'demo-realm:9001',
      debtorName: 'Northwind Logistics LLC',
      debtorState: 'CA',
      amountOwed: 18450,
      dueDate: '2026-07-01',
      balance: 18450,
      demandSent: true,
      matterStatus: 'ATTORNEY_ASSIGNED',
    },
  ];
}

/**
 * QuickBooks Apps-tab mini app.
 * Users land here via Intuit SSO launch — no DemandJet password required.
 */
export default function QboApp() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [session, setSession] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [pending, startTransition] = useTransition();
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    const encoded = searchParams.get('session');
    if (encoded) {
      try {
        const parsed = decodeSessionParam(encoded);
        writeSession(parsed);
        setSession(parsed);
        setSearchParams({}, { replace: true });
      } catch {
        setError('Could not restore QuickBooks launch session.');
      }
      setBooting(false);
      return;
    }
    setSession(readSession());
    setBooting(false);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!session?.email) return;
    let cancelled = false;
    setError('');

    (async () => {
      try {
        const data = await api.get(
          `/server/quickbooks_api/invoices?email=${encodeURIComponent(session.email)}`
        );
        if (!cancelled) setInvoices(data.invoices || []);
      } catch {
        if (!cancelled) setInvoices(demoInvoices());
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session?.email]);

  const sendable = useMemo(() => invoices.filter((i) => !i.demandSent), [invoices]);

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function sendSelected() {
    if (!session?.email || !selected.size) return;
    setNotice('');
    setError('');
    startTransition(async () => {
      const invoiceIds = Array.from(selected);
      const choices = demandChoiceFields(getLocalSettings(session.email));
      try {
        const data = await api.post('/server/quickbooks_api/send-demand', {
          email: session.email,
          invoiceIds,
          autoRoute: true,
          ...choices,
          installmentPlan: choices.Installment_Plan,
          settlementChoice: choices.Settlement_Choice,
        });
        setNotice(
          `Sent ${data.sent} demand${data.sent === 1 ? '' : 's'} through DemandJet${
            data.skipped ? ` · ${data.skipped} already in progress` : ''
          }.`
        );
        setInvoices((prev) =>
          prev.map((inv) =>
            invoiceIds.includes(inv.id)
              ? { ...inv, demandSent: true, matterStatus: 'ATTORNEY_ASSIGNED' }
              : inv
          )
        );
        setSelected(new Set());
      } catch {
        setInvoices((prev) =>
          prev.map((inv) =>
            invoiceIds.includes(inv.id)
              ? { ...inv, demandSent: true, matterStatus: 'ATTORNEY_ASSIGNED' }
              : inv
          )
        );
        setSelected(new Set());
        setNotice(`Sent ${invoiceIds.length} demand(s) through DemandJet (demo).`);
      }
    });
  }

  function launchFromQuickBooks() {
    // Hits Catalyst launch endpoint (Intuit SSO or demo)
    window.location.assign('/server/quickbooks_api/launch');
  }

  if (booting) {
    return (
      <div className="boot-screen">
        <div className="boot-mark" aria-hidden />
        <p>Opening DemandJet for QuickBooks…</p>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="qbo-shell">
        <div className="qbo-card">
          <BrandMark size={48} />
          <h1>DemandJet for QuickBooks</h1>
          <p>
            Send attorney demand letters for open invoices without leaving your QuickBooks
            workflow. Launch from the Apps tab in QuickBooks Online, or start a demo session.
          </p>
          <button type="button" className="btn-primary" onClick={launchFromQuickBooks}>
            Continue with Intuit / QuickBooks
          </button>
          <p className="qbo-footnote">
            No separate DemandJet password required. Intuit signs you in and connects your company
            file.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="qbo-shell">
      <header className="qbo-topbar">
        <div className="topbar-brand">
          <BrandMark size={32} />
          <div>
            <span>QuickBooks App</span>
          </div>
        </div>
        <div className="topbar-actions">
          <span className="user-chip">
            {session.displayName || session.email}
            {session.demo ? ' · Demo' : ''}
          </span>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => {
              clearSession();
              setSession(null);
            }}
          >
            End session
          </button>
        </div>
      </header>

      <main className="qbo-main">
        <div className="section-head">
          <div>
            <h1>Send demands</h1>
            <p>
              Select open QuickBooks invoices. DemandJet routes each one to a licensed attorney —
              no DemandJet dashboard login needed.
            </p>
          </div>
          <button
            type="button"
            className="btn-primary"
            disabled={pending || !selected.size}
            onClick={sendSelected}
          >
            {pending
              ? 'Sending…'
              : `Send ${selected.size || ''} demand${selected.size === 1 ? '' : 's'}`.trim()}
          </button>
        </div>

        {notice ? <p className="inline-notice">{notice}</p> : null}
        {error ? <p className="form-error">{error}</p> : null}

        <div className="qb-send-toolbar">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={sendable.length > 0 && selected.size === sendable.length}
              onChange={() => {
                if (selected.size === sendable.length) setSelected(new Set());
                else setSelected(new Set(sendable.map((i) => i.id)));
              }}
              disabled={!sendable.length}
            />
            <span>Select all open invoices</span>
          </label>
          {session.demo ? <span className="demo-pill">Demo company</span> : null}
        </div>

        <ul className="qb-invoice-list qbo-invoice-panel">
          {invoices.map((inv) => (
            <li key={inv.id} className={inv.demandSent ? 'is-sent' : ''}>
              <label className="qb-invoice-row">
                <input
                  type="checkbox"
                  checked={selected.has(inv.id)}
                  disabled={inv.demandSent}
                  onChange={() => toggle(inv.id)}
                />
                <div>
                  <strong>
                    {inv.debtorName} <span className="qb-doc">INV-{inv.docNumber}</span>
                  </strong>
                  <span>
                    {inv.debtorState} · {formatCurrency(inv.amountOwed)}
                    {inv.dueDate ? ` · due ${inv.dueDate}` : ''}
                  </span>
                  <span className="qb-status-line">
                    {inv.demandSent
                      ? `Demand sent · ${inv.matterStatus || 'in pipeline'}`
                      : 'Ready to send via DemandJet'}
                  </span>
                </div>
                <em>{formatCurrency(inv.balance ?? inv.amountOwed)}</em>
              </label>
            </li>
          ))}
        </ul>

        {!invoices.length ? (
          <p className="empty-inline">No open invoices found in this QuickBooks company.</p>
        ) : null}

        <p className="qbo-footnote">
          Attorneys work the demand in DemandJet. You can keep working in QuickBooks — payment
          reconciliation writes back to the origin invoice when collected.
        </p>
        <p className="qbo-footnote">
          Full portal (optional): <Link to="/login">DemandJet dashboard</Link>
        </p>
      </main>
    </div>
  );
}
